import React from 'react'
import { observer } from 'mobx-react-lite'
import { CustomTableStore, useCustomTableStore } from '../CustomTableStore'
import { CustomTableLocalRow } from '../CustomTableLocalRow'
import TableCell from '@mui/material/TableCell'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import { stylesRowClosedNoBorder } from './shared-styles'
import useCustomTableRenderLog from '../hooks/useCustomTableRenderLog'
import useStore from 'reusable/mobx-store-utils'
import useClasses from 'theme/UseClasses'

interface CustomTableCollapsibleDetailsProps<RowData extends AnyObject, RowStore> {
   localRow: CustomTableLocalRow<RowData, RowStore>
}

export const CustomTableCollapsibleDetails = observer(function CustomTableCollapsibleDetails
   <RowData extends AnyObject, RowStore>(
      props: CustomTableCollapsibleDetailsProps<RowData, RowStore>
   ) {
   useCustomTableRenderLog('CustomTableCollapsibleDetails')
   const classes = useClasses(stylesRowClosedNoBorder)

   const customTableStore = useCustomTableStore<CustomTableStore<RowData, RowStore>>()

   const localStore = useStore(
      sp => ({
         get collapsibleDetailStyling() {
            const style = {
               ...(sp.localRow.collapsibleConfig || {}).collapsibleDetailStyling,
            } as React.CSSProperties
            if (!sp.localRow.collapsibleOpen) {
               style.paddingTop = '0px'
               style.paddingBottom = '0px'
            }
            style.transitionProperty = 'padding-top, padding-bottom'
            style.transitionDuration = '0.2s'
            return style
         },
         get trStyling() {
            if (sp.localRow.collapsibleOpen) {
               return {}
            } else {
               return {
                  height: '0px',
               }
            }
         },
         get collapsibleRender() {
            if (sp.localRow.collapsibleConfig.render) {
               const CollapsibleConfigRender = sp.localRow.collapsibleConfig.render
               return (
                  <CollapsibleConfigRender
                     opened={!!sp.localRow.collapsibleOpen}
                     row={sp.localRow.row}
                  />
               )
            } else {
               return null
            }
         },
         get cellDivStyle(): React.CSSProperties {
            return sp.localRow.collapsibleOpen
               ? {
                  maxHeight: 10000,
                  transition: 'max-height 0.1s',
               }
               : {
                  maxHeight: 0,
                  boxSizing: 'border-box',
                  overflow: 'hidden',
                  transition: 'max-height 0.1s',
               }
         },
      }),
      { customTableStore, ...props }
   )

   if (!customTableStore.hasCollapsibleRowDetail) {
      return null
   }

   const cellClass = props.localRow.collapsibleOpen ? classes.cellOpened : classes.cellClosed

   return (
      <TableBody>
         <TableRow>
            <TableCell
               className={cellClass}
               style={localStore.collapsibleDetailStyling}
               colSpan={customTableStore.sp.columns.length + 2}>
               <div style={localStore.cellDivStyle}>{localStore.collapsibleRender}</div>
            </TableCell>
         </TableRow>
      </TableBody>
   )
}
)
